import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

/**
 * Shared table shell: a bordered white card whose contents scroll horizontally
 * when the table is wider than the viewport. Reused wherever a table is rendered
 * so the scroll guard and card styling live in one place.
 *
 * An optional `footer` (e.g. a pagination bar) renders *outside* the horizontal
 * scroll zone, pinned to the bottom across the card's full width, so it stays put
 * when the table scrolls sideways.
 */
export default function TableCard({
  children,
  footer,
  fillHeight = false,
}: {
  children: ReactNode
  footer?: ReactNode
  /** Fill the parent's remaining height and scroll inside it, so the sideways scrollbar sits at the
   *  bottom of the screen instead of below the last row. The parent must be a flex column. */
  fillHeight?: boolean
}) {
  return (
    <div className={cn('bg-white rounded-xl border border-gray-200 overflow-hidden', fillHeight && 'flex flex-col flex-1 min-h-0')}>
      {/* relative: absolutely-positioned cells content (e.g. sr-only labels) stays inside the scroll
          zone instead of widening the page on a phone */}
      <div className={cn('relative', fillHeight ? 'flex-1 min-h-0 overflow-auto' : 'overflow-x-auto')}>{children}</div>
      {footer && <div className="border-t border-gray-200">{footer}</div>}
    </div>
  )
}
